import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus, ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface TagTreeNode {
  id: string;
  label: string;
  /** Real tag this row maps to. Rows without one are grouping-only: their
   *  checkbox is a tri-state select-all over descendants. */
  tag?: string | null;
  children?: TagTreeNode[];
}

interface TagTreeSelectorProps {
  /** Tree to render — hand-managed on the ZeeVee Groups tab. */
  tree: TagTreeNode[];
  /** Currently selected tag values. */
  value: string[];
  /** Emitted whenever the selection changes (array of tag strings). */
  onChange: (value: string[]) => void;
  title?: string;
  className?: string;
  /** Shown when there are no tags to display. */
  emptyIndicator?: React.ReactNode;
}

type TreeNode = TagTreeNode;

const hasKids = (n: TreeNode) => !!n.children && n.children.length > 0;

/** Collect every selectable leaf tag beneath a node. */
function leafTags(node: TreeNode): string[] {
  let out: string[] = [];
  (node.children || []).forEach((c) => {
    if (c.tag && !hasKids(c)) out.push(c.tag);
    else out = out.concat(leafTags(c));
  });
  return out;
}

/** Tri-state checkbox using the same tokens as the shadcn Checkbox. */
function TriStateCheckbox({
  checked,
  indeterminate,
  onCheckedChange,
  label,
}: {
  checked: boolean;
  indeterminate: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <CheckboxPrimitive.Root
      checked={indeterminate ? 'indeterminate' : checked}
      onCheckedChange={(v) => onCheckedChange(v === true)}
      aria-label={label}
      className={cn(
        'peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
        'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground'
      )}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        {indeterminate ? (
          <Minus className="h-3.5 w-3.5" />
        ) : (
          <Check className="h-3.5 w-3.5" />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export function TagTreeSelector({
  tree,
  value,
  onChange,
  title = 'Tags',
  className,
  emptyIndicator,
}: TagTreeSelectorProps) {
  const selectedSet = React.useMemo(() => new Set(value), [value]);

  // Branches are open by default; we only track which ones are collapsed.
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());

  const allBranchIds = React.useMemo(() => {
    const ids: string[] = [];
    const walk = (ns: TreeNode[]) =>
      ns.forEach((n) => {
        if (hasKids(n)) {
          ids.push(n.id);
          walk(n.children!);
        }
      });
    walk(tree);
    return ids;
  }, [tree]);

  const isOpen = (id: string) => !collapsed.has(id);
  const toggleOpen = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(allBranchIds));

  const emit = (next: Set<string>) => onChange([...next]);

  const toggleTag = (tag: string, checked: boolean) => {
    const next = new Set(selectedSet);
    if (checked) next.add(tag);
    else next.delete(tag);
    emit(next);
  };

  const toggleGroup = (node: TreeNode, checked: boolean) => {
    const next = new Set(selectedSet);
    leafTags(node).forEach((t) => (checked ? next.add(t) : next.delete(t)));
    emit(next);
  };

  // Aggregate selection state over a node's descendant leaf tags.
  const leafState = (node: TreeNode): { all: boolean; any: boolean } => {
    if (node.tag && !hasKids(node)) {
      const on = selectedSet.has(node.tag);
      return { all: on, any: on };
    }
    let all = true;
    let any = false;
    (node.children || []).forEach((c) => {
      const s = leafState(c);
      all = all && s.all;
      any = any || s.any;
    });
    return { all, any };
  };

  const renderNode = (node: TreeNode, depth: number): React.ReactNode => {
    const kids = hasKids(node);
    const open = isOpen(node.id);
    const isGroup = !node.tag;
    const showCheckbox = isGroup || !!node.tag;

    let checked = false;
    let indeterminate = false;
    if (isGroup) {
      const s = leafState(node);
      checked = s.any && s.all;
      indeterminate = s.any && !s.all;
    } else if (node.tag) {
      checked = selectedSet.has(node.tag);
    }

    const onToggle = (next: boolean) => {
      if (isGroup) toggleGroup(node, next);
      else if (node.tag) toggleTag(node.tag, next);
    };

    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-2 rounded-md py-1.5 pr-2 hover:bg-accent"
          style={{ paddingLeft: 8 + depth * 20 }}
        >
          {kids ? (
            <button
              type="button"
              onClick={() => toggleOpen(node.id)}
              aria-label={open ? 'Collapse' : 'Expand'}
              className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground"
            >
              {open ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          ) : (
            <span className="inline-block h-4 w-4 shrink-0" />
          )}

          {showCheckbox && (
            <TriStateCheckbox
              checked={checked}
              indeterminate={indeterminate}
              onCheckedChange={onToggle}
              label={node.label}
            />
          )}

          <span
            onClick={showCheckbox ? () => onToggle(!checked) : undefined}
            className={cn(
              'select-none text-sm',
              showCheckbox && 'cursor-pointer',
              (isGroup || kids) && 'font-medium'
            )}
          >
            {node.label}
          </span>

          {node.tag && kids && (
            <span className="font-mono text-xs text-muted-foreground">
              {node.tag}
            </span>
          )}
          {isGroup && (
            <span className="text-xs text-muted-foreground">
              ({leafTags(node).length})
            </span>
          )}
        </div>

        {kids && open && node.children!.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'rounded-md border bg-card text-card-foreground',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="text-sm font-medium">{title}</span>
        {value.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {value.length} selected
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 px-2 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={expandAll}
        >
          Expand all
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={collapseAll}
        >
          Collapse all
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 text-xs"
          onClick={() => onChange([])}
          disabled={value.length === 0}
        >
          Clear
        </Button>
      </div>

      <div className="max-h-[60vh] overflow-auto px-1 pb-2">
        {tree.length ? (
          tree.map((n) => renderNode(n, 0))
        ) : (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {emptyIndicator ?? 'No tags'}
          </div>
        )}
      </div>
    </div>
  );
}

export default TagTreeSelector;
